if (!customElements.get('subscription-widget')) {
  customElements.define(
    'subscription-widget',
    class SubscriptionWidget extends HTMLElement {
      constructor() {
        super();
        this.handleAddToCartClick = this.handleAddToCartClick.bind(this);
        this.handleCartFormSubmit = this.handleCartFormSubmit.bind(this);
        this.handleSellingPlanInput = this.handleSellingPlanInput.bind(this);
        this.handleSellingPlanInputChange = this.handleSellingPlanInputChange.bind(this);
        this.onFormChange = this.onFormChange.bind(this);
        this.installCartAddPatch();
      }

      connectedCallback() {
        this.teardown();
        this.productRoot = this.closest('[theme-product-main]');
        this.main_product_form =
          this.closest('form[action*="/cart/add"]') ||
          this.productRoot?.querySelector('form[action*="/cart/add"]') ||
          document.querySelector('.product__page form[action*="/cart/add"]') ||
          document.querySelector('.product form[action*="/cart/add"]') ||
          document.querySelector('form[action*="/cart/add.js"]') ||
          document.querySelector('form[action*="/cart/add"]');

        this.productData = this.parseProductData();
        this.currentVariant = this.parseVariantData() || this.getVariantFromForm() || {};
        this.sectionId = this.dataset.sectionId;

        if (this.main_product_form && !this.main_product_form.querySelector('input[name="selling_plan"]')) {
          this.sellingPlanFormInput = document.createElement('input');
          this.sellingPlanFormInput.type = 'hidden';
          this.sellingPlanFormInput.name = 'selling_plan';
          this.main_product_form.appendChild(this.sellingPlanFormInput);
        } else if (this.main_product_form) {
          this.sellingPlanFormInput = this.main_product_form.querySelector('input[name="selling_plan"]');
        }

        this.querySelectorAll('input[name="purchase_type"]').forEach((input) => {
          input.addEventListener('change', this.handleSellingPlanInput);
        });

        const planSelect = this.querySelector('.deployer-select');
        if (planSelect) {
          planSelect.addEventListener('change', this.handleSellingPlanInputChange);
        }

        document.addEventListener('change', this.onFormChange);

        document.addEventListener('click', this.handleAddToCartClick, true);
        document.addEventListener('submit', this.handleCartFormSubmit, true);

        this.syncPlanOptions(this.getAllocations(), this.currentVariant);
        this.ensureSelection();
        this.handleSellingPlanInput();
        this.watchCtaPrices();
      }

      disconnectedCallback() {
        this.teardown();
      }

      teardown() {
        document.removeEventListener('change', this.onFormChange);
        document.removeEventListener('click', this.handleAddToCartClick, true);
        document.removeEventListener('submit', this.handleCartFormSubmit, true);
        this.ctaObserver?.disconnect();
        this.ctaObserver = null;
        clearTimeout(this._ctaPriceTimer);
      }

      parseProductData() {
        try {
          const raw = this.productRoot?.dataset.product;
          return raw ? JSON.parse(raw) : null;
        } catch (error) {
          return null;
        }
      }

      parseVariantData() {
        try {
          const jsonEl = this.querySelector('[data-variant-json]');
          return jsonEl ? JSON.parse(jsonEl.textContent) : null;
        } catch (error) {
          return null;
        }
      }

      onFormChange(event) {
        if (!event.target.matches('[name="option1"], [name="option2"], [name="option3"]')) return;
        const fromProduct = this.productRoot?.contains(event.target);
        const fromSticky = event.target.closest('[theme-sticky-atc-form]');
        if (!fromProduct && !fromSticky) return;
        const variant = this.getVariantFromForm(event.target.form);
        if (!variant || String(variant.id) === String(this.currentVariant?.id)) return;
        this.renderForVariant(variant);
      }

      getVariantFromForm(form = this.main_product_form) {
        const variants = this.productData?.variants;
        if (!variants || !form) return null;

        const selected = { option1: '', option2: '', option3: '' };
        new FormData(form).forEach((value, key) => {
          if (key in selected) selected[key] = String(value);
        });

        return (
          variants.find((variant) => {
            const optionCount = [variant.option1, variant.option2, variant.option3].filter(Boolean).length;
            if (optionCount <= 1) return !selected.option1 || variant.option1 === selected.option1;
            if (optionCount === 2) {
              return variant.option1 === selected.option1 && variant.option2 === selected.option2;
            }
            return (
              variant.option1 === selected.option1 &&
              variant.option2 === selected.option2 &&
              variant.option3 === selected.option3
            );
          }) || null
        );
      }

      getAllocations(variant = this.currentVariant) {
        return variant?.selling_plan_allocations || [];
      }

      renderForVariant(variant) {
        this.currentVariant = variant;
        this.dataset.variantId = variant.id;

        const allocations = this.getAllocations(variant);
        const hasSubscription = allocations.length > 0;
        const subOption = this.getSelectedSubscriptionOption();
        const oneTimeOption = this.querySelector(
          '.subscription-widget__option:not(.subscription-widget__option--subscription)'
        );
        const subInput = subOption?.querySelector('input[name="purchase_type"]');

        if (!hasSubscription) {
          this.setSelectedOption(oneTimeOption);
          subOption?.classList.add('subscription-widget__option--disabled');
          if (subInput) subInput.disabled = true;
        } else {
          subOption?.classList.remove('subscription-widget__option--disabled');
          if (subInput) subInput.disabled = false;
        }

        this.syncPlanOptions(allocations, variant);
        this.renderOptionPrice(
          this.querySelector('[data-price-role="one-time"]'),
          variant.price,
          variant.compare_at_price
        );

        if (hasSubscription) {
          const selectedPlanOption = this.getActivePlanOption();
          const subscriptionPrice = this.getAdjustedPrice(selectedPlanOption) || allocations[0]?.price || variant.price;
          this.renderOptionPrice(
            this.querySelector('[data-price-role="subscription"]'),
            subscriptionPrice,
            variant.compare_at_price > subscriptionPrice ? variant.compare_at_price : variant.price
          );
          this.updateSubscribeSavingPercentage(subscriptionPrice);
        }

        this.ensureSelection();
        this.handleSellingPlanInput();
      }

      syncPlanOptions(allocations, variant) {
        const select = this.querySelector('.deployer-select');
        if (!select) return;

        let firstAvailableIndex = -1;
        [...select.options].forEach((option, index) => {
          const allocation = allocations.find((item) => String(item.selling_plan_id) === String(option.dataset.value));
          option.hidden = false;
          option.disabled = false;
          if (allocation) {
            option.dataset.price = allocation.price;
            if (firstAvailableIndex < 0) firstAvailableIndex = index;
          } else {
            option.dataset.price = variant.price;
          }
        });

        if (firstAvailableIndex >= 0 && (select.selectedIndex < 0 || !select.options[select.selectedIndex])) {
          select.selectedIndex = firstAvailableIndex;
        }
      }

      getActivePlanOption() {
        const select = this.querySelector('.deployer-select');
        if (!select || select.selectedIndex < 0) return null;
        return select.options[select.selectedIndex] || null;
      }

      ensureSelection() {
        const subOption = this.getSelectedSubscriptionOption();
        const oneTimeOption = this.querySelector(
          '.subscription-widget__option:not(.subscription-widget__option--subscription)'
        );
        const hasSubscription = subOption && !subOption.classList.contains('subscription-widget__option--disabled');
        const current = this.querySelector('.subscription-widget__option--selected');
        const currentIsValid = current && !current.classList.contains('subscription-widget__option--disabled');
        const checked = this.querySelector('input[name="purchase_type"]:checked:not(:disabled)');

        if (!hasSubscription) {
          this.setSelectedOption(oneTimeOption);
          return;
        }

        if (checked) {
          this.setSelectedOption(checked.closest('.subscription-widget__option'));
          return;
        }

        if (currentIsValid) {
          this.setSelectedOption(current);
          return;
        }

        if (this.dataset.defaultSelected === 'subscription') {
          this.setSelectedOption(subOption);
          return;
        }

        this.setSelectedOption(oneTimeOption);
      }

      renderOptionPrice(container, price, compareAt) {
        if (!container || typeof formatPrice !== 'function') return;
        const numericPrice = Number(price || 0);
        const numericCompare = Number(compareAt || 0);
        const hasDiscount = numericCompare > numericPrice;
        const saving = hasDiscount ? Math.floor(((numericCompare - numericPrice) / numericCompare) * 100) : 0;

        let compareEl = container.querySelector('s');
        if (!compareEl) {
          compareEl = document.createElement('s');
          container.prepend(compareEl);
        }
        compareEl.textContent = hasDiscount ? formatPrice(numericCompare) : '';
        compareEl.toggleAttribute('hidden', !hasDiscount);

        let current = container.querySelector('.subscription-widget__option-price-current');
        if (!current) {
          current = document.createElement('span');
          current.className = 'subscription-widget__option-price-current';
          container.append(current);
        }
        current.textContent = formatPrice(numericPrice);

        const dynamicDiscount = container.querySelector('.subscription-widget__dynamic-discount');
        let badge = container.querySelector('.subscription-widget__option-price-badge');
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'subscription-widget__option-price-badge';
          container.append(badge);
        }
        if (dynamicDiscount) {
          if (!badge.contains(dynamicDiscount)) {
            badge.replaceChildren('-', dynamicDiscount);
          }
          badge.hidden = false;
        } else if (saving > 0) {
          badge.textContent = `-${saving}%`;
          badge.hidden = false;
        } else {
          badge.hidden = true;
        }
      }

      setSelectedOption(option) {
        if (!option) return;
        this.querySelectorAll('.subscription-widget__option').forEach((item) => {
          item.classList.remove('subscription-widget__option--selected');
          const input = item.querySelector('input[name="purchase_type"]');
          if (input) input.checked = false;
        });
        option.classList.add('subscription-widget__option--selected');
        const input = option.querySelector('input[name="purchase_type"]');
        if (input && !input.disabled) input.checked = true;
      }

      getSelectedSubscriptionOption() {
        return this.querySelector('.subscription-widget__option--subscription');
      }

      getSelectedSellingPlanId() {
        const selectedOption = this.querySelector('.subscription-widget__option--selected');
        if (!selectedOption?.classList.contains('subscription-widget__option--subscription')) return '';
        if (selectedOption.classList.contains('subscription-widget__option--disabled')) return '';

        const select = selectedOption.querySelector('.deployer-select');
        const option = select?.options[select.selectedIndex];
        if (!option || option.hidden) return '';
        return option.dataset.value || '';
      }

      handleSellingPlanInputChange(e) {
        this.updateSubscribePrices(e);
        this.handleSellingPlanInput(e);
      }

      updateSubscribePrices(e) {
        const selectedOption = e.target.options[e.target.selectedIndex];
        const adjustedPrice = this.getAdjustedPrice(selectedOption);
        this.renderOptionPrice(
          this.querySelector('[data-price-role="subscription"]'),
          adjustedPrice,
          this.currentVariant.compare_at_price > adjustedPrice
            ? this.currentVariant.compare_at_price
            : this.currentVariant.price
        );
        this.updateSubscribeSavingPercentage(adjustedPrice);
      }

      getAdjustedPrice(selectedOption) {
        if (selectedOption?.dataset.price) return parseFloat(selectedOption.dataset.price);

        const adjustementType = selectedOption?.dataset.ajustmentType;
        const adjustementValue = parseFloat(selectedOption?.dataset.ajustmentValue);

        switch (adjustementType) {
          case 'fixed_amount':
            return this.currentVariant.price - adjustementValue;
          case 'percentage':
            return this.currentVariant.price * (1 - adjustementValue / 100);
          case 'price':
            return adjustementValue;
          default:
            return this.currentVariant.price;
        }
      }

      updateSubscribeSavingPercentage(finalPrice) {
        const savingPercentageContainers = this.querySelectorAll('.subscription-widget__dynamic-discount');
        const variantPrice = this.currentVariant.price / 100;
        finalPrice = finalPrice / 100;
        if (!savingPercentageContainers.length || !variantPrice || variantPrice === finalPrice) return;
        savingPercentageContainers.forEach((container) => {
          const saving = ((variantPrice - finalPrice) / variantPrice) * 100;
          container.innerText = Math.round(saving) + '%';
        });
      }

      handleSellingPlanInput(e) {
        const selectedOption =
          e?.target.closest('.subscription-widget__option') ||
          this.querySelector('.subscription-widget__option--selected');
        if (!selectedOption) return;

        if (selectedOption.classList.contains('subscription-widget__option--disabled')) {
          this.setSelectedOption(
            this.querySelector('.subscription-widget__option:not(.subscription-widget__option--subscription)')
          );
          this.syncCartState('');
          this.syncSellingPlan('');
          this.syncAddToCartButtons('');
          this.updateDisplayedPrices(this.currentVariant.price);
          return;
        }

        this.setSelectedOption(selectedOption);

        if (selectedOption.classList.contains('subscription-widget__option--subscription')) {
          const select = selectedOption.querySelector('.deployer-select');
          const planId = this.getSelectedSellingPlanId();
          const adjustedPrice = this.getAdjustedPrice(select?.options[select.selectedIndex]);
          this.syncCartState(planId);
          this.syncSellingPlan(planId);
          this.syncAddToCartButtons(planId);
          this.updateDisplayedPrices(adjustedPrice);
        } else {
          this.syncCartState('');
          this.syncSellingPlan('');
          this.syncAddToCartButtons('');
          this.updateDisplayedPrices(this.currentVariant.price);
        }
      }

      syncCartState(planId, variantId = this.currentVariant.id) {
        window.subscriptionWidgetCartState = {
          variantId: String(variantId || this.currentVariant.id),
          sellingPlan: String(planId || ''),
          updatedAt: Date.now(),
        };
      }

      syncSellingPlan(planId) {
        document.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
          let input = form.querySelector('input[name="selling_plan"]');

          if (!input && planId) {
            input = document.createElement('input');
            input.type = 'hidden';
            input.name = 'selling_plan';
            form.appendChild(input);
          }

          if (!input) return;

          input.value = planId || '';
          input.disabled = !planId;
        });
      }

      syncAddToCartButtons(planId) {
        document.querySelectorAll('[theme-addtocart][data-addtocart-id1]').forEach((button) => {
          this.syncAddToCartButton(button, planId);
        });
      }

      syncAddToCartButton(button, planId) {
        if (!button) return;

        if (planId) {
          button.dataset.addtocartPlan1 = planId;
        } else {
          delete button.dataset.addtocartPlan1;
        }
      }

      handleAddToCartClick(event) {
        const button = event.target.closest('[theme-addtocart][data-addtocart-id1]');
        if (!button) return;

        const planId = this.getSelectedSellingPlanId();
        this.syncCartState(planId, button.dataset.addtocartId1);
        this.syncSellingPlan(planId);
        this.syncAddToCartButton(button, planId);
      }

      handleCartFormSubmit(event) {
        if (!event.target.matches('form[action*="/cart/add"]')) return;

        const planId = this.getSelectedSellingPlanId();
        const variantId = event.target.querySelector('[name="id"]')?.value;
        this.syncCartState(planId, variantId);
        this.syncSellingPlan(planId);
      }

      installCartAddPatch() {
        if (window.subscriptionWidgetCartAddPatched || typeof window.fetch !== 'function') return;

        window.subscriptionWidgetCartAddPatched = true;
        const originalFetch = window.fetch.bind(window);

        const getLiveSellingPlan = () => {
          const widget = document.querySelector('subscription-widget');
          const selectedOption = widget?.querySelector('.subscription-widget__option--selected');
          if (!selectedOption?.classList.contains('subscription-widget__option--subscription')) return '';
          if (selectedOption.classList.contains('subscription-widget__option--disabled')) return '';

          const select = selectedOption.querySelector('.deployer-select');
          const option = select?.options[select.selectedIndex];
          if (!option || option.hidden) return '';
          return option.dataset.value || '';
        };

        window.fetch = (resource, options = {}) => {
          const url = typeof resource === 'string' ? resource : resource?.url;

          if (!url?.includes('/cart/add') || !options.body) {
            return originalFetch(resource, options);
          }

          try {
            const payload = JSON.parse(options.body);
            const items = Array.isArray(payload.items) ? payload.items : [payload];
            const sellingPlan = getLiveSellingPlan();

            items.forEach((item) => {
              if (sellingPlan) {
                item.selling_plan = Number(sellingPlan);
              } else {
                delete item.selling_plan;
              }
            });

            return originalFetch(resource, {
              ...options,
              body: JSON.stringify(Array.isArray(payload.items) ? { ...payload, items } : items[0]),
            });
          } catch (error) {
            return originalFetch(resource, options);
          }
        };
      }

      getPriceContainers() {
        return document.querySelectorAll(
          '[data-subscription-widget-price], [data-subscription-widget-atc-price]'
        );
      }

      watchCtaPrices() {
        if (this.ctaObserver) return;
        this.ctaObserver = new MutationObserver(() => {
          if (this._applyingCtaPrice || this._displayedPrice == null) return;
          this.renderDisplayedPrices(this._displayedPrice);
        });
        this.getPriceContainers().forEach((el) => {
          this.ctaObserver.observe(el, { childList: true, subtree: true, characterData: true });
        });
      }

      updateDisplayedPrices(price) {
        this._displayedPrice = price;
        this.renderDisplayedPrices(this._displayedPrice);
        // Sharp updateVariantPrice runs after us and overwrites .price — re-apply once it is done.
        clearTimeout(this._ctaPriceTimer);
        this._ctaPriceTimer = setTimeout(() => this.renderDisplayedPrices(this._displayedPrice), 0);
      }

      renderDisplayedPrices(price) {
        if (price == null || typeof formatPrice !== 'function') return;

        const compareAtPrice =
          price < this.currentVariant.price
            ? this.currentVariant.price
            : this.currentVariant.compare_at_price || 0;
        const hasDiscount = compareAtPrice > price;
        const discountRate = hasDiscount ? Math.floor(((compareAtPrice - price) / compareAtPrice) * 100) : 0;
        const formattedPrice = formatPrice(price);
        const html = `
              <div class="flex flex-wrap items-center gap-1" data-display-discount="true">
                ${hasDiscount ? `<del class="compare-price">${formatPrice(compareAtPrice)}</del>` : ''}
                <span class="regular-price">${formattedPrice}</span>
                ${hasDiscount ? `<span class="discount-rate">-${discountRate}%</span>` : ''}
              </div>
            `;

        this._applyingCtaPrice = true;
        this.getPriceContainers().forEach((container) => {
          const current = container.querySelector('.regular-price')?.textContent;
          if (!container.querySelector('.price') && current === formattedPrice) return;
          container.innerHTML = html;
        });
        queueMicrotask(() => {
          this._applyingCtaPrice = false;
        });
      }
    }
  );
}
